import axios from 'axios';

// Use relative URL when in development to work with proxy
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'https://api.prepstop.com')
  : ''; // Empty string means use proxy in development

// Type Definitions
export interface CommunityKPIs {
  totalReferred: number;
  hivTestsCompleted: number;
  telehealthCompleted: number;
  activeOnPrep: number;
  pending: number;
  conversionRate: number;
  monthlyPerformance: {
    referrals: number;
    completed: number;
  };
  monthlyGrowth: Array<{
    month: string;
    count: number;
  }>;
}

export interface CommunityPatient {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  market: string;
  display_status: string;
  referral_source: string | null;
  referral_notes: string | null;
  intake_block_b_completed: boolean;
  hiv_test_completed: boolean;
  hiv_test_result: 'NEGATIVE' | 'POSITIVE' | 'INCONCLUSIVE' | null;
  hiv_test_date: string | null;
  hiv_test_due_date: string | null;
  hiv_test_documents?: Array<{filename: string, url: string, uploadedAt: string, size: number, mimeType: string}> | null;
  labs_status: 'NOT_STARTED' | 'SCHEDULING' | 'SCHEDULED' | 'AWAITING_RESULTS' | 'COMPLETED' | null;
  labs_scheduled_date: string | null;
  insurance_status?: string | null;
  insurance_verified?: boolean;
  insurance_documents?: Array<{filename: string, url: string, uploadedAt: string}>;
  insurance_documents_uploaded_at?: string;
  telehealth_completed: boolean;
  prescription_issued: boolean;
  medication_shipped: boolean;
  prep_type: string;
  daysSinceReferral: number;
  createdAt: string;
  updatedAt: string;
  currentCase: {
    id: number;
    status: string;
  } | null;
}

export interface HivResultData {
  testDate: string;
  result: 'NEGATIVE' | 'POSITIVE' | 'INCONCLUSIVE';
  notes?: string;
}

class CommunityDashboardService {
  private baseURL = `${API_BASE_URL}/api/dashboard/community`;

  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      withCredentials: true, // Include cookies for session-based auth
    };
  }

  /**
   * Get community partner KPIs
   */
  async getKPIs(): Promise<CommunityKPIs> {
    const response = await axios.get(`${this.baseURL}/kpis`, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Get referred patients
   */
  async getPatients(): Promise<CommunityPatient[]> {
    const response = await axios.get(`${this.baseURL}/patients`, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Upload HIV test result with files
   */
  async uploadHivResult(patientId: number, data: HivResultData | FormData): Promise<{ success: boolean; message: string }> {
    const headers = this.getAuthHeaders();
    
    // If data is FormData, set appropriate content type
    if (data instanceof FormData) {
      headers.headers['Content-Type'] = 'multipart/form-data';
    }
    
    const response = await axios.post(`${this.baseURL}/upload-hiv-result/${patientId}`, data, headers);
    return response.data;
  }

  /**
   * Update lab workflow status
   */
  async updateLabStatus(patientId: number, status: string, scheduledDate?: string): Promise<{ success: boolean; message: string }> {
    const response = await axios.post(`${this.baseURL}/update-lab-status/${patientId}`, {
      status,
      scheduledDate
    }, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Get single patient detail (uses existing admin endpoint)
   */
  async getPatientDetail(patientId: number): Promise<any> {
    const response = await axios.get(`${API_BASE_URL}/api/dashboard/patients/${patientId}`, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Upload insurance documents to S3
   */
  async uploadInsuranceDocuments(patientId: number, files: File[]): Promise<{ success: boolean; message: string; documents: any[] }> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('documents', file);
    });

    const token = localStorage.getItem('authToken');
    const response = await axios.post(`${this.baseURL}/upload-insurance-documents/${patientId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      withCredentials: true,
    });
    return response.data;
  }

  /**
   * Update PrepCase status through HIV test workflow
   */
  async updatePrepCaseStatus(patientId: number, status: string): Promise<{ success: boolean; message: string; prepCase: any }> {
    const response = await axios.post(`${this.baseURL}/update-prep-case-status/${patientId}`, {
      status
    }, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Get specific community partner KPIs (Super Admin only)
   */
  async getPartnerKPIs(partnerId: number): Promise<CommunityKPIs> {
    const response = await axios.get(`${this.baseURL}/${partnerId}/kpis`, this.getAuthHeaders());
    return response.data;
  }

  /**
   * Get specific community partner patients (Super Admin only)
   */
  async getPartnerPatients(partnerId: number): Promise<CommunityPatient[]> {
    const response = await axios.get(`${this.baseURL}/${partnerId}/patients`, this.getAuthHeaders());
    return response.data;
  }
}

const communityDashboardService = new CommunityDashboardService();
export default communityDashboardService;
